import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useMatchRoute, useNavigate, useParams } from '@tanstack/react-router';
import type { ProjectId, SquadId } from '@voll-intelligence/domain';

import { queryKeys } from '@web/core/query';
import {
  flightAlertsRepository,
  FlightAlertsDisabledError,
  overviewRepository,
  projectsRepository,
  type ProjectWithPermissions,
} from '@web/data/repositories';
import { useAuthz } from '@web/ui/core/hooks/use_authz';
import { useOrgContext, type AppIconName } from '@web/ui/core';
import { fireAndForget } from '@web/ui/core/utils';

const SIDEBAR_ALERTS_FILTERS = { severity: 'all', vip: false, q: '' } as const;
const SIDEBAR_ALERTS_REFETCH_MS = 60_000;
// Stand-in while the projects load: one identity, so the sidebar tree memoizes across renders.
const NO_PROJECTS: ProjectWithPermissions[] = [];

type OrgSidebarSquad = {
  id: SquadId;
  name: string;
  isActive: boolean;
  openCount: number;
  canEdit: boolean;
};

export type OrgSidebarProject = {
  id: ProjectId;
  name: string;
  icon: AppIconName;
  isActive: boolean;
  openCount: number;
  canEdit: boolean;
  squads: OrgSidebarSquad[];
};

export type OrgSidebarState = {
  isReady: boolean;
  isOverviewActive: boolean;
  isTriageActive: boolean;
  isAlertsActive: boolean;
  isMiceActive: boolean;
  overviewCount: number;
  triageCount: number;
  alertsCount: number;
  alertsEnabled: boolean;
  canSeeMice: boolean;
  projects: OrgSidebarProject[];
  canCreateProject: boolean;
  /** "Ver como viajante" — granted by member flag, only in the VOLL org. */
  canViewAs: boolean;
  goOverview: () => void;
  goTriage: () => void;
  goAlerts: () => void;
  goMice: () => void;
  goViewAs: () => void;
  goNewProject: () => void;
  goEditProject: (projectId: ProjectId) => void;
  goCreateSquad: (projectId: ProjectId) => void;
  goSquad: (squadId: SquadId) => void;
  goEditSquad: (squadId: SquadId) => void;
};

/**
 * Drives the org workspace sidebar: project tree, squad counts,
 * permission gates and navigation handlers. The view stays
 * presentational — all data shaping (counts aggregation, active
 * detection, permission flags) happens here.
 */
export function useOrgSidebar(): OrgSidebarState {
  const { orgId, orgSlug } = useOrgContext();
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();
  const params = useParams({ strict: false });
  const authz = useAuthz();

  const projectsQuery = useQuery({
    queryKey: queryKeys.projects.list(orgId),
    queryFn: () => projectsRepository.loadProjects(orgId),
  });
  const countsQuery = useQuery({
    queryKey: queryKeys.overview.sidebarCounts(orgId),
    queryFn: () => overviewRepository.loadSidebarCounts(orgId),
  });
  // The panel reads card data, and the route it leads to is card data too:
  // a caller without the permission would poll an aggregate kanban-api will
  // refuse, and get a nav item leading nowhere.
  const canReadAlerts = authz.has('kanban:card:read');
  const alertsQuery = useQuery({
    queryKey: queryKeys.alerts.flights(orgId, SIDEBAR_ALERTS_FILTERS),
    queryFn: () => flightAlertsRepository.list(orgId, SIDEBAR_ALERTS_FILTERS),
    // Same reasoning as the alerts page (`ui/alerts/views/alerts_page.tsx`):
    // one immediate retry for a one-off blip, none for the disabled-feature
    // error (permanent until a deploy); no backoff delay so a test doesn't
    // have to wait it out.
    retry: (failureCount, error) =>
      !(error instanceof FlightAlertsDisabledError) && failureCount < 1,
    retryDelay: 0,
    // The nav item simply disappears when this errors (see `alertsEnabled`
    // below) — `GlobalErrorHandler` must not ALSO toast a global error on
    // every page just because this background poll failed.
    meta: { silent: true },
    // Stop polling every 60s once we know the org has the panel turned off.
    refetchInterval: (q) =>
      q.state.error instanceof FlightAlertsDisabledError ? false : SIDEBAR_ALERTS_REFETCH_MS,
    enabled: Boolean(orgId) && canReadAlerts,
  });

  const rawProjects = projectsQuery.data ?? NO_PROJECTS;
  const activeSquadId = params.squadId ?? null;

  const projects = useMemo<OrgSidebarProject[]>(() => {
    const squadCounts = countsQuery.data?.squads ?? {};
    return rawProjects.map((p) => {
      const canEditProject = p.permissions.includes('kanban:projects:update');
      const squads: OrgSidebarSquad[] = p.squads.map((s) => ({
        id: s.id,
        name: s.name,
        isActive: s.id === activeSquadId,
        openCount: squadCounts[s.id] ?? 0,
        canEdit: canEditProject,
      }));
      return {
        id: p.id,
        name: p.name,
        icon: (p.icon || 'kanban') as AppIconName,
        isActive: squads.some((s) => s.isActive),
        openCount: squads.reduce((sum, s) => sum + s.openCount, 0),
        canEdit: canEditProject,
        squads,
      };
    });
  }, [rawProjects, countsQuery.data, activeSquadId]);

  const isTriageActive = !!matchRoute({ to: '/orgs/$orgSlug/triage', fuzzy: true });
  const isAlertsActive = !!matchRoute({ to: '/orgs/$orgSlug/alerts', fuzzy: true });
  const isMiceActive = !!matchRoute({ to: '/orgs/$orgSlug/mice', fuzzy: true });
  const alertsEnabled = canReadAlerts && !(alertsQuery.error instanceof FlightAlertsDisabledError);
  const alertsCount = alertsQuery.data
    ? alertsQuery.data.counts.act + alertsQuery.data.counts.watch
    : 0;

  const goOverview = (): void => {
    fireAndForget(navigate({ to: '/orgs/$orgSlug/overview', params: { orgSlug } }));
  };
  const goTriage = (): void => {
    fireAndForget(navigate({ to: '/orgs/$orgSlug/triage', params: { orgSlug } }));
  };
  const goAlerts = (): void => {
    fireAndForget(navigate({ to: '/orgs/$orgSlug/alerts', params: { orgSlug } }));
  };
  const goMice = (): void => {
    fireAndForget(navigate({ to: '/orgs/$orgSlug/mice', params: { orgSlug } }));
  };
  const goViewAs = (): void => {
    fireAndForget(navigate({ to: '/orgs/$orgSlug/view-as', params: { orgSlug } }));
  };
  const goNewProject = (): void => {
    fireAndForget(navigate({ to: '/orgs/$orgSlug/project/new', params: { orgSlug } }));
  };
  const goEditProject = (projectId: ProjectId): void => {
    fireAndForget(
      navigate({
        to: '/orgs/$orgSlug/project/$projectId',
        params: { orgSlug, projectId },
      }),
    );
  };
  const goCreateSquad = (projectId: ProjectId): void => {
    fireAndForget(
      navigate({
        to: '/orgs/$orgSlug/squad/new',
        params: { orgSlug },
        search: { projectId },
      }),
    );
  };
  const goSquad = (squadId: SquadId): void => {
    fireAndForget(
      navigate({
        to: '/orgs/$orgSlug/squad/$squadId',
        params: { orgSlug, squadId },
      }),
    );
  };
  const goEditSquad = (squadId: SquadId): void => {
    fireAndForget(
      navigate({
        to: '/orgs/$orgSlug/squad/$squadId/edit',
        params: { orgSlug, squadId },
      }),
    );
  };

  return {
    isReady: !!projectsQuery.data,
    isOverviewActive: !activeSquadId && !isTriageActive && !isAlertsActive && !isMiceActive,
    isTriageActive,
    isAlertsActive,
    isMiceActive,
    overviewCount: countsQuery.data?.overview ?? 0,
    triageCount: countsQuery.data?.triage ?? 0,
    alertsCount,
    alertsEnabled,
    canSeeMice: authz.has('mice:read'),
    projects,
    canCreateProject: authz.has('kanban:projects:create'),
    canViewAs: authz.has('kanban:traveler:view_as'),
    goOverview,
    goTriage,
    goAlerts,
    goMice,
    goViewAs,
    goNewProject,
    goEditProject,
    goCreateSquad,
    goSquad,
    goEditSquad,
  };
}
